import { DashboardBatch, DashboardAnalyticsFilters, BatchedAnalyticsResponse, ChartDataPoint } from '../types';
import { API_CONFIG } from '../config/api';
import { authApi } from '../services/api';
// Import ExcelExportService dynamically to avoid SSR issues and import conflicts
import { pdfExportService } from '../services/PDFExportService.backend';
import { formatCurrency } from './currency';

type OverallStats = {
  totalRevenue: number;
  totalMembers: number;
  averageRevenuePerMember: number;
  memberGrowthRate: number;
  revenueGrowthRate: number;
  activityBreakdown: {
    contributions: number;
    benefits: number;
    events: number;
    expenses: number;
  };
  percentiles?: {
    revenue_50th: number;
    revenue_90th: number;
    revenue_95th: number;
    revenue_99th: number;
  };
};

// Simple Event Emitter for browser
class EventEmitter {
  private events: { [key: string]: ((data: any) => void)[] } = {};

  on(event: string, listener: (data: any) => void) {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
  }

  off(event: string, listener: (data: any) => void) {
    if (this.events[event]) {
      this.events[event] = this.events[event].filter(l => l !== listener);
    }
  }

  emit(event: string, data: any) {
    if (this.events[event]) {
      this.events[event].forEach(listener => listener(data));
    }
  }
}

interface AnalyticsState {
  // Data
  batches: DashboardBatch[];
  chartData: ChartDataPoint[];
  overallStats: OverallStats;
  isLoading: boolean;
  error: string | null;

  // Filters
  filters: DashboardAnalyticsFilters;

  // Real-time
  isRealTimeEnabled: boolean;
  refreshInterval: number;

  // Chart interactions
  selectedMetric: string;
  selectedTimeRange: string;

  // Drill-down
  drillDownData: unknown;
  isDrillDownActive: boolean;

  // Performance monitoring
  performanceMetrics: {
    loadTime: number;
    renderTime: number;
    dataPoints: number;
  };
}

class AnalyticsUtility {
  private emitter = new EventEmitter();
  private state: AnalyticsState;
  private realTimeInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.state = {
      batches: [],
      chartData: [],
      overallStats: {
        totalRevenue: 0,
        totalMembers: 0,
        averageRevenuePerMember: 0,
        memberGrowthRate: 0,
        revenueGrowthRate: 0,
        activityBreakdown: {
          contributions: 0,
          benefits: 0,
          events: 0,
          expenses: 0
        }
      },
      isLoading: false,
      error: null,
      filters: {
        dateRange: {
          start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          end: new Date().toISOString()
        },
        townId: 'all',
        activityType: 'all',
        batchLevel: '1_week'
      },
      isRealTimeEnabled: false,
      refreshInterval: 30000,
      selectedMetric: 'revenue',
      selectedTimeRange: '30d',
      drillDownData: null,
      isDrillDownActive: false,
      performanceMetrics: {
        loadTime: 0,
        renderTime: 0,
        dataPoints: 0
      }
    };
  }

  // Getters
  getState() {
    return { ...this.state };
  }

  // Subscribe to state changes
  subscribe(listener: (state: AnalyticsState) => void) {
    this.emitter.on('stateChange', listener);
    return () => this.emitter.off('stateChange', listener);
  }

  // Update state and emit event
  private updateState(updates: Partial<AnalyticsState>) {
    this.state = { ...this.state, ...updates };
    this.emitter.emit('stateChange', this.getState());
  }

  // Methods
  setFilters(filters: DashboardAnalyticsFilters) {
    this.updateState({ filters });
    this.fetchData();
  }

  setIsRealTimeEnabled(enabled: boolean) {
    this.updateState({ isRealTimeEnabled: enabled });
    this.setupRealTime(enabled);
  }

  setRefreshInterval(interval: number) {
    this.updateState({ refreshInterval: interval });
    if (this.state.isRealTimeEnabled) {
      this.setupRealTime(true);
    }
  }

  setSelectedMetric(metric: string) {
    this.updateState({ selectedMetric: metric });
  }

  setSelectedTimeRange(range: string) {
    this.updateState({ selectedTimeRange: range });
  }

  setDrillDownData(data: unknown) {
    this.updateState({ drillDownData: data });
  }

  setIsDrillDownActive(active: boolean) {
    this.updateState({ isDrillDownActive: active });
  }

  private setupRealTime(enabled: boolean) {
    if (this.realTimeInterval) {
      clearInterval(this.realTimeInterval);
      this.realTimeInterval = null;
    }

    if (enabled) {
      this.realTimeInterval = setInterval(() => {
        this.fetchData();
      }, this.state.refreshInterval);
    }
  }

  async fetchData() {
    this.updateState({ isLoading: true, error: null });

    const startTime = performance.now();
    // Try both token storage methods for backward compatibility
    const authToken = localStorage.getItem('auth_token') || localStorage.getItem('token');

    try {
      if (!authToken) {
        throw new Error('No authentication token found. Please log in again.');
      }

      const queryParams = new URLSearchParams();

      if (this.state.filters.dateRange) {
        queryParams.append('startDate', this.state.filters.dateRange.start);
        queryParams.append('endDate', this.state.filters.dateRange.end);
      }

      if (this.state.filters.townId && this.state.filters.townId !== 'all') {
        queryParams.append('townId', this.state.filters.townId);
      }

      if (this.state.filters.batchLevel) {
        queryParams.append('batchLevel', this.state.filters.batchLevel);
      }

      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DASHBOARD}/analytics?${queryParams.toString()}`;

      console.log('AnalyticsUtility: Fetching data with params:', {
        filters: this.state.filters,
        queryParams: queryParams.toString(),
        apiUrl,
        tokenLength: authToken.length
      });

      const fetchWithToken = async (token: string) =>
        fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

      let response = await fetchWithToken(authToken);

      if (response.status === 401 && localStorage.getItem('refresh_token')) {
        const refreshResponse = await authApi.refreshToken(
          localStorage.getItem('refresh_token')!,
        );
        const refreshedToken = refreshResponse.success && refreshResponse.data
          ? (refreshResponse.data as { token?: string }).token
          : undefined;

        if (refreshedToken) {
          localStorage.setItem('auth_token', refreshedToken);
          response = await fetchWithToken(refreshedToken);
        }
      }

      console.log('AnalyticsUtility: Response status:', response.status);

      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const result = await response.json() as BatchedAnalyticsResponse;

      console.log('AnalyticsUtility: Raw API response:', result);

      if (result.success) {
        const batches = result.data?.batches || [];
        const apiOverallStats = result.data?.overallStats;
        
        // Validate that we actually have data
        const hasValidData = batches.length > 0 || (apiOverallStats && (
          apiOverallStats.totalRevenue > 0 ||
          apiOverallStats.totalMembers > 0 ||
          apiOverallStats.activityBreakdown?.contributions > 0
        ));

        const overallStats = {
          totalRevenue: apiOverallStats?.totalRevenue || 0,
          totalMembers: apiOverallStats?.totalMembers || 0,
          averageRevenuePerMember: apiOverallStats?.averageRevenuePerMember || 0,
          memberGrowthRate: apiOverallStats?.memberGrowthRate || 0,
          revenueGrowthRate: apiOverallStats?.revenueGrowthRate || 0,
          activityBreakdown: {
            contributions: apiOverallStats?.activityBreakdown?.contributions || 0,
            benefits: apiOverallStats?.activityBreakdown?.benefits || 0,
            events: apiOverallStats?.activityBreakdown?.events || 0,
            expenses: apiOverallStats?.activityBreakdown?.expenses || 0
          },
          percentiles: apiOverallStats?.percentiles
        };

        console.log('AnalyticsUtility: Processing batches:', {
          batchCount: batches.length,
          hasValidData: hasValidData,
          overallStats: overallStats
        });

        const transformedChartData = this.transformToChartData(batches);

        this.updateState({
          batches,
          chartData: transformedChartData,
          overallStats,
          performanceMetrics: {
            ...this.state.performanceMetrics,
            dataPoints: batches.length,
            loadTime: performance.now() - startTime
          }
        });

        console.log('AnalyticsUtility: Data successfully loaded and set');
      } else {
        console.error('AnalyticsUtility: API returned error:', result.error);
        this.updateState({
          error: result.error || 'Failed to load analytics data',
          batches: [],
          chartData: [{
            time: 'No data',
            revenue: 0,
            members: 0,
            activities: 0,
            contributions: 0,
            benefits: 0,
            events: 0,
            expenses: 0
          }],
          overallStats: {
            totalRevenue: 0,
            totalMembers: 0,
            averageRevenuePerMember: 0,
            memberGrowthRate: 0,
            revenueGrowthRate: 0,
            activityBreakdown: {
              contributions: 0,
              benefits: 0,
              events: 0,
              expenses: 0
            }
          }
        });
      }
    } catch (err) {
      console.error('AnalyticsUtility: Analytics fetch error:', err);

      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      this.updateState({
        error: `Error loading analytics data: ${errorMessage}`,
        batches: [],
        chartData: [{
          time: 'No data available',
          revenue: 0,
          members: 0,
          activities: 0,
          contributions: 0,
          benefits: 0,
          events: 0,
          expenses: 0
        }],
        overallStats: {
          totalRevenue: 0,
          totalMembers: 0,
          averageRevenuePerMember: 0,
          memberGrowthRate: 0,
          revenueGrowthRate: 0,
          activityBreakdown: {
            contributions: 0,
            benefits: 0,
            events: 0,
            expenses: 0
          }
        }
      });
    } finally {
      this.updateState({ isLoading: false });
      console.log('AnalyticsUtility: Fetch operation completed');
    }
  }

  transformToChartData(batches: DashboardBatch[]): ChartDataPoint[] {
    if (!batches || batches.length === 0) {
      return [{
        time: 'No data',
        revenue: 0,
        members: 0,
        activities: 0,
        contributions: 0,
        benefits: 0,
        events: 0,
        expenses: 0
      }];
    }

    try {
      const transformedData = batches.map(batch => {
        return {
          time: this.formatTimeForChart(batch.timeRange?.start || new Date().toISOString()),
          revenue: Number(batch.summary?.totalRevenue) || 0,
          members: Number(batch.summary?.totalMembers) || 0,
          activities: Number(batch.summary?.totalActivities) || 0,
          contributions: Number(batch.activityBreakdown?.contributions) || 0,
          benefits: Number(batch.activityBreakdown?.benefits) || 0,
          events: Number(batch.activityBreakdown?.events) || 0,
          expenses: Number(batch.activityBreakdown?.expenses) || 0
        };
      }).reverse();

      console.log('AnalyticsUtility: Successfully transformed data:', transformedData);
      return transformedData;
    } catch (error) {
      console.error('AnalyticsUtility: Error transforming chart data:', error);
      return [{
        time: 'Error loading data',
        revenue: 0,
        members: 0,
        activities: 0,
        contributions: 0,
        benefits: 0,
        events: 0,
        expenses: 0
      }];
    }
  }

  formatTimeForChart(startTime: string): string {
    const date = new Date(startTime);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  }

  async exportDashboard(format: 'pdf' | 'excel' | 'png') {
    try {
      console.log(`AnalyticsUtility: Exporting dashboard as ${format}...`);
      console.log(`AnalyticsUtility: Current chartData length: ${this.state.chartData.length}`);
      console.log(`AnalyticsUtility: Current overallStats:`, this.state.overallStats);
      console.log(`AnalyticsUtility: Current filters:`, this.state.filters);

      await this.performDirectExport(format, this.state.chartData, this.state.overallStats, this.state.filters);

      const exportEvent = new CustomEvent('dashboardExport', {
        detail: { format, chartData: this.state.chartData, overallStats: this.state.overallStats, batches: this.state.batches, filters: this.state.filters }
      });

      window.dispatchEvent(exportEvent);

      console.log(`AnalyticsUtility: Export completed for ${format}`);
    } catch (err) {
      console.error('AnalyticsUtility: Export error:', err);
      this.updateState({ error: 'Failed to export dashboard' });
      throw err;
    }
  }

  private async performDirectExport(format: 'pdf' | 'excel' | 'png', chartData: ChartDataPoint[], overallStats: OverallStats, filters: DashboardAnalyticsFilters) {
    console.log(`AnalyticsUtility: Performing direct ${format} export`);

    switch (format) {
      case 'excel':
        await this.performDirectExcelExport(chartData, overallStats, filters);
        break;
      case 'pdf':
        await this.performDirectPDFExport(chartData, overallStats, filters);
        break;
      case 'png':
        await this.performDirectPNGExport(chartData, overallStats, filters);
        break;
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  }

  private async performDirectExcelExport(chartData: ChartDataPoint[], overallStats: OverallStats, filters: DashboardAnalyticsFilters) {
    try {
      console.log('AnalyticsUtility: Starting direct Excel export...');

      // Import ExcelExportService dynamically to avoid SSR issues
      const { excelExportService } = await import('../services/ExcelExportService');

      const workbookData = [
        {
          'Report Type': 'Analytics Dashboard Export',
          'Generated Date': new Date().toLocaleString(),
          'Date Range': `${filters.dateRange?.start ? new Date(filters.dateRange.start).toLocaleDateString() : 'N/A'} - ${filters.dateRange?.end ? new Date(filters.dateRange.end).toLocaleDateString() : 'N/A'}`,
          'Town': filters.townId === 'all' ? 'All Towns' : (filters.townId || 'Unknown Town'),
          'Total Revenue': overallStats.totalRevenue || 0,
          'Total Members': overallStats.totalMembers || 0,
          'Average Revenue per Member': overallStats.averageRevenuePerMember || 0,
          'Member Growth Rate (%)': overallStats.memberGrowthRate || 0,
          'Revenue Growth Rate (%)': overallStats.revenueGrowthRate || 0
        },
        {
          'Activity Type': 'Contributions',
          'Count': overallStats.activityBreakdown?.contributions || 0,
          'Details': 'Member contributions to the system'
        },
        {
          'Activity Type': 'Benefits',
          'Count': overallStats.activityBreakdown?.benefits || 0,
          'Details': 'Member benefits and assistance'
        },
        {
          'Activity Type': 'Events',
          'Count': overallStats.activityBreakdown?.events || 0,
          'Details': 'Community events organized'
        },
        {
          'Activity Type': 'Expenses',
          'Count': overallStats.activityBreakdown?.expenses || 0,
          'Details': 'System operational expenses'
        }
      ];

      const detailedData = chartData.map(point => ({
        'Time Period': point.time,
        'Revenue': point.revenue || 0,
        'Members': point.members || 0,
        'Activities': point.activities || 0,
        'Contributions': point.contributions || 0,
        'Benefits': point.benefits || 0,
        'Events': point.events || 0,
        'Expenses': point.expenses || 0
      }));

      const filename = `Dashboard_Analytics_${new Date().toISOString().slice(0, 10)}`;
      const excelBlob = await excelExportService.generateSimpleExcel(
        [...workbookData, { '': '' }, ...detailedData],
        filename,
        'Dashboard Analytics'
      );

      const url = URL.createObjectURL(excelBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.xlsx`;
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('AnalyticsUtility: Direct Excel export completed');
    } catch (error) {
      console.error('AnalyticsUtility: Direct Excel export failed:', error);
      throw error;
    }
  }

  private async performDirectPDFExport(chartData: ChartDataPoint[], overallStats: OverallStats, filters: DashboardAnalyticsFilters) {
    try {
      console.log('AnalyticsUtility: Starting direct PDF export...');

      const chartDataForPDF = chartData.map(point => ({
        time: point.time,
        revenue: point.revenue || 0,
        members: point.members || 0,
        activities: point.activities || 0,
        contributions: point.contributions || 0,
        benefits: point.benefits || 0,
        events: point.events || 0,
        expenses: point.expenses || 0
      }));

      let pdfBlob = await pdfExportService.generateReportWithChartsData(
        chartDataForPDF,
        chartDataForPDF,
        'financial',
        {
          header: {
            title: 'Dashboard Analytics Report',
            dateRange: `${filters.dateRange?.start ? new Date(filters.dateRange.start).toLocaleDateString() : 'N/A'} - ${filters.dateRange?.end ? new Date(filters.dateRange.end).toLocaleDateString() : 'N/A'}`,
            townName: filters.townId === 'all' ? 'All Towns' : (filters.townId || 'Unknown Town'),
            generatedDate: new Date().toLocaleDateString()
          },
          styles: {
            colors: {
              primary: '#2563eb',
              secondary: '#1e40af',
              text: '#1f2937',
              background: '#ffffff',
              accent: '#f3f4f6'
            },
            fonts: {
              header: 'Roboto',
              body: 'Roboto',
              monospace: 'Courier'
            }
          },
          layout: {
            margins: { top: 40, right: 40, bottom: 40, left: 40 },
            columns: 12,
            pageOrientation: 'portrait'
          }
        }
      );

      if (pdfBlob instanceof Blob) {
        console.log('AnalyticsUtility: PDF Blob received successfully');
      } else {
        console.log('AnalyticsUtility: PDF data structure received, need to generate actual PDF');
        const jsPDF = (await import('jspdf')).default;
        const doc = new jsPDF();

        doc.setFontSize(16);
        doc.text('Dashboard Analytics Report', 20, 20);
        doc.setFontSize(12);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 30);
        doc.text(`Town: ${filters.townId === 'all' ? 'All Towns' : (filters.townId || 'Unknown Town')}`, 20, 40);

        doc.setFontSize(14);
        doc.text('Summary', 20, 50);
        doc.setFontSize(12);
        doc.text(`Total Revenue: ${formatCurrency(overallStats.totalRevenue || 0)}`, 20, 60);
        doc.text(`Total Members: ${(overallStats.totalMembers || 0).toLocaleString()}`, 20, 70);

        const pdfContent = doc.output('blob');
        pdfBlob = pdfContent;
      }

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Dashboard_Analytics_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('AnalyticsUtility: Direct PDF export completed');
    } catch (error) {
      console.error('AnalyticsUtility: Direct PDF export failed:', error);
      throw error;
    }
  }

  private async performDirectPNGExport(chartData: ChartDataPoint[], overallStats: OverallStats, filters: DashboardAnalyticsFilters) {
    try {
      console.log('AnalyticsUtility: Starting direct PNG export...');

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      const scale = 2;
      canvas.width = 1000 * scale;
      canvas.height = 700 * scale;
      ctx.scale(scale, scale);

      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = '#1f2937';
      ctx.font = 'bold 28px Arial';
      ctx.fillText('Ghana Town Membership System', 20, 40);
      ctx.font = 'bold 20px Arial';
      ctx.fillText('Dashboard Analytics Report', 20, 70);

      ctx.font = '14px Arial';
      ctx.fillStyle = '#6b7280';
      ctx.fillText(`Generated: ${new Date().toLocaleString()}`, 20, 100);
      ctx.fillText(`Town: ${filters.townId === 'all' ? 'All Towns' : filters.townId}`, 20, 120);

      ctx.font = 'bold 18px Arial';
      ctx.fillStyle = '#1f2937';
      ctx.fillText('Key Metrics', 20, 160);

      ctx.font = '14px Arial';
      ctx.fillStyle = '#374151';
      const metrics = [
        `Total Revenue: ${formatCurrency(overallStats.totalRevenue || 0)}`,
        `Total Members: ${(overallStats.totalMembers || 0).toLocaleString()}`,
        `Avg Revenue/Member: ${formatCurrency(overallStats.averageRevenuePerMember || 0)}`,
        `Member Growth: ${overallStats.memberGrowthRate?.toFixed(2) || '0'}%`,
        `Revenue Growth: ${overallStats.revenueGrowthRate?.toFixed(2) || '0'}%`,
        `Total Activities: ${(overallStats.activityBreakdown?.contributions || 0) + (overallStats.activityBreakdown?.benefits || 0) + (overallStats.activityBreakdown?.events || 0) + (overallStats.activityBreakdown?.expenses || 0)}`
      ];

      metrics.forEach((metric, index) => {
        ctx.fillText(metric, 20, 190 + (index * 25));
      });

      ctx.font = 'bold 18px Arial';
      ctx.fillStyle = '#1f2937';
      ctx.fillText('Activity Breakdown', 500, 160);

      ctx.font = '14px Arial';
      ctx.fillStyle = '#374151';
      const activities = [
        `Contributions: ${overallStats.activityBreakdown?.contributions || 0}`,
        `Benefits: ${overallStats.activityBreakdown?.benefits || 0}`,
        `Events: ${overallStats.activityBreakdown?.events || 0}`,
        `Expenses: ${overallStats.activityBreakdown?.expenses || 0}`
      ];

      activities.forEach((activity, index) => {
        ctx.fillText(activity, 500, 190 + (index * 25));
      });

      if (chartData.length > 0) {
        ctx.font = 'bold 16px Arial';
        ctx.fillStyle = '#1f2937';
        ctx.fillText('Revenue Trend (Last 10 periods)', 20, 380);

        const chartX = 20;
        const chartY = 400;
        const chartWidth = 460;
        const chartHeight = 200;

        ctx.strokeStyle = '#e5e7eb';
        ctx.strokeRect(chartX, chartY, chartWidth, chartHeight);

        ctx.strokeStyle = '#f3f4f6';
        for (let i = 1; i < 5; i++) {
          const y = chartY + (i * chartHeight / 5);
          ctx.beginPath();
          ctx.moveTo(chartX, y);
          ctx.lineTo(chartX + chartWidth, y);
          ctx.stroke();
        }

        const maxRevenue = Math.max(...chartData.slice(0, 10).map(d => d.revenue || 0));
        const barWidth = chartWidth / Math.min(chartData.length, 10) - 4;

        chartData.slice(0, 10).forEach((point, index) => {
          const barHeight = maxRevenue > 0 ? (point.revenue || 0) / maxRevenue * (chartHeight - 20) : 0;
          const x = chartX + 2 + (index * (barWidth + 4));
          const y = chartY + chartHeight - barHeight - 10;

          const gradient = ctx.createLinearGradient(x, y, x, y + barHeight);
          gradient.addColorStop(0, '#3b82f6');
          gradient.addColorStop(1, '#1d4ed8');
          ctx.fillStyle = gradient;
          ctx.fillRect(x, y, barWidth, barHeight);
        });

        ctx.font = '12px Arial';
        ctx.fillStyle = '#6b7280';
        ctx.textAlign = 'right';
        for (let i = 0; i <= 5; i++) {
          const value = (maxRevenue * i / 5).toLocaleString();
          const y = chartY + chartHeight - (i * chartHeight / 5);
          ctx.fillText(`${formatCurrency(Number(value.replace(/,/g, '')))}`, chartX - 5, y + 4);
        }
        ctx.textAlign = 'left';
      }

      ctx.font = '12px Arial';
      ctx.fillStyle = '#9ca3af';
      ctx.fillText('Generated by Ghana Town Membership System', 20, 650);

      const link = document.createElement('a');
      link.download = `Dashboard_Analytics_${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      console.log('AnalyticsUtility: Direct PNG export completed');
    } catch (error) {
      console.error('AnalyticsUtility: Direct PNG export failed:', error);
      throw error;
    }
  }

  refreshData() {
    console.log('AnalyticsUtility: Manually refreshing data...');
    this.fetchData();
  }

  // Initialize on creation
  init() {
    this.fetchData();
  }
}

// Export singleton instance — do NOT auto-init; callers must invoke init() after authentication
const analyticsUtility = new AnalyticsUtility();

export { analyticsUtility };